import { useMemo, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import LinkedDevicesConfig, { LinkedDevice, linkedDevicesToJson, parseLinkedDevices } from './LinkedDevicesConfig';
import { LfdIndicatorOption } from './LFDPeripheralConfig';
import PortConfigComponent, { buildDefaultPortConfig, PortConfig } from './PortConfig';
import { PORT_CONFIG_OPTIONS } from '../../../utils/portConfigs';

// Mirrors Weightrax.Domain.Enums.RelayEvent (integer-serialized)
export const RelayEvent = {
  ApplicationStart: 1,
  VehicleArrived: 2,
  VehicleOnBridge: 3,
  ValidVehicle: 4,
  InvalidVehicle: 5,
  TransactionSaved: 6,
  VehicleOffBridge: 7,
  VehicleDeparted: 8,
  ApplicationShutdown: 9,
  VehicleArrivalExpired: 10
} as const;

type RelayEventValue = (typeof RelayEvent)[keyof typeof RelayEvent];

const ALL_RELAY_EVENTS: { value: RelayEventValue; label: string }[] = [
  { value: RelayEvent.ApplicationStart, label: 'Application Start' },
  { value: RelayEvent.VehicleArrived, label: 'Vehicle Arrived' },
  { value: RelayEvent.VehicleArrivalExpired, label: 'Vehicle Arrival Expired' },
  { value: RelayEvent.VehicleOnBridge, label: 'Vehicle On Bridge' },
  { value: RelayEvent.ValidVehicle, label: 'Valid Vehicle' },
  { value: RelayEvent.InvalidVehicle, label: 'Invalid Vehicle' },
  { value: RelayEvent.TransactionSaved, label: 'Transaction Saved' },
  { value: RelayEvent.VehicleOffBridge, label: 'Vehicle Off Bridge' },
  { value: RelayEvent.VehicleDeparted, label: 'Vehicle Departed' },
  { value: RelayEvent.ApplicationShutdown, label: 'Application Shutdown' }
];

// Mirrors Weightrax.Domain.Enums.RelayAction (integer-serialized)
export const RelayAction = {
  Up: 1,
  Down: 2
} as const;

type RelayActionValue = (typeof RelayAction)[keyof typeof RelayAction];

// Mirrors Weightrax.Domain.Enums.DirectionEnum
const DIRECTION_OPTIONS = [
  { value: 1, label: 'In' },
  { value: 2, label: 'Out' },
  { value: 3, label: 'Both' }
];

// Mirrors Weightrax.Domain.Enums.BridgePositionEnum
const POSITION_OPTIONS = [
  { value: 1, label: 'Start of bridge' },
  { value: 2, label: 'End of bridge' }
];

export type RelayCommand = {
  command: string;
  description: string;
};

export type RelayEventSubscription = {
  event: RelayEventValue;
  action: RelayActionValue;
  /** Seconds to hold before switching back; null latches until the next event. */
  durationSeconds: number | null;
  repeatCount: number;
};

// Mirrors Weightrax.Domain.Models.Peripherals.RelayDefinition
export type RelayConfig = {
  name: string;
  portConfig: PortConfig;
  direction: number;
  position: number;
  portNo: number;
  upCommand: RelayCommand;
  downCommand: RelayCommand;
  /** Blank command means the relay has no status command. */
  readStatusCommand: RelayCommand;
  subscriptions: RelayEventSubscription[];
  linkedDevices: LinkedDevice[];
  /** The weight indicator whose vehicle tracking drives this relay; null is the first indicator on the device. */
  indicatorPeripheralId: string | null;
  /** The indicator's platform (configured scale, by position); null is the first. */
  scaleIndex: number | null;
};

type RelayPeripheralConfigProps = {
  value: RelayConfig;
  onChange: (next: RelayConfig) => void;
  /** Heading for the section, eg. "Barrier Arm". */
  title: string;
  /** The site's indicators, for choosing which vehicle tracking drives this relay. */
  indicators?: LfdIndicatorOption[];
  disabled?: boolean;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/**
 * Reads a field written in camelCase (this editor) or PascalCase (the Local API, which saves the
 * definition as C# serialises it).
 */
function getField(record: Record<string, unknown>, camel: string): unknown {
  return record[camel] !== undefined ? record[camel] : record[camel.charAt(0).toUpperCase() + camel.slice(1)];
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** Reads an enum written by number (this editor) or by name (the Local API). */
function asEnum(value: unknown, options: Record<string, number>, fallback: number): number {
  if (typeof value === 'number' && Object.values(options).includes(value)) return value;
  if (typeof value === 'string') {
    const key = Object.keys(options).find((k) => k.toLowerCase() === value.trim().toLowerCase());
    if (key) return options[key];
  }
  return fallback;
}

/** Blank reads as no indicator: the id is a Ulid on the device, and an empty one would not deserialise. */
function asIdOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function asIndexOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

function buildCommand(source: unknown, fallback: RelayCommand): RelayCommand {
  const rec = asRecord(source);
  if (Object.keys(rec).length === 0) return { ...fallback };
  return {
    command: asString(getField(rec, 'command')),
    description: asString(getField(rec, 'description'))
  };
}

function buildSubscription(source: unknown): RelayEventSubscription {
  const rec = asRecord(source);
  const duration = getField(rec, 'durationSeconds');
  return {
    event: asEnum(getField(rec, 'event'), RelayEvent, RelayEvent.ApplicationStart) as RelayEventValue,
    action: asEnum(getField(rec, 'action'), RelayAction, RelayAction.Up) as RelayActionValue,
    durationSeconds: typeof duration === 'number' && Number.isFinite(duration) && duration > 0 ? duration : null,
    repeatCount: Math.max(1, asNumber(getField(rec, 'repeatCount'), 1))
  };
}

export function buildDefaultRelayConfig(base?: unknown): RelayConfig {
  const source = asRecord(base);
  const subscriptions = getField(source, 'subscriptions');

  return {
    name: asString(getField(source, 'name')),
    portConfig: buildDefaultPortConfig(getField(source, 'portConfig') ?? { type: 'TcpIp' }),
    direction: asEnum(getField(source, 'direction'), { In: 1, Out: 2, Both: 3 }, 1),
    position: asEnum(getField(source, 'position'), { Start: 1, End: 2 }, 1),
    portNo: asNumber(getField(source, 'portNo'), 1),
    upCommand: buildCommand(getField(source, 'upCommand'), { command: 'FF0101', description: 'Up' }),
    downCommand: buildCommand(getField(source, 'downCommand'), { command: 'FF0100', description: 'Down' }),
    readStatusCommand: buildCommand(getField(source, 'readStatusCommand'), { command: '', description: '' }),
    subscriptions: Array.isArray(subscriptions) ? subscriptions.map(buildSubscription) : [],
    linkedDevices: parseLinkedDevices(source),
    indicatorPeripheralId: asIdOrNull(getField(source, 'indicatorPeripheralId')),
    scaleIndex: asIndexOrNull(getField(source, 'scaleIndex'))
  };
}

/** The configuration as stored on the peripheral and read by the device as a RelayDefinition. */
export function relayConfigToJson(value: RelayConfig): unknown {
  return {
    name: value.name,
    portConfig: value.portConfig,
    direction: value.direction,
    position: value.position,
    portNo: value.portNo,
    upCommand: value.upCommand,
    downCommand: value.downCommand,
    readStatusCommand: value.readStatusCommand.command.trim() ? value.readStatusCommand : null,
    subscriptions: value.subscriptions,
    linkedDevices: linkedDevicesToJson(value.linkedDevices),
    indicatorPeripheralId: value.indicatorPeripheralId,
    scaleIndex: value.scaleIndex
  };
}

const RELAY_PORT_TYPES = [PORT_CONFIG_OPTIONS.Serial, PORT_CONFIG_OPTIONS.TcpIp, PORT_CONFIG_OPTIONS.Http, PORT_CONFIG_OPTIONS.Mock];

/** Hex is tidied to upper case; anything else (eg. a web relay's status.xml) is sent as typed. */
function normaliseCommand(raw: string): string {
  const compact = raw.replace(/\s+/g, '');
  return /^[0-9a-f]*$/i.test(compact) ? compact.toUpperCase() : compact;
}

function CommandFields({
  label,
  value,
  onChange,
  disabled,
  isHttp
}: {
  label: string;
  value: RelayCommand;
  onChange: (next: RelayCommand) => void;
  disabled: boolean;
  isHttp: boolean;
}) {
  return (
    <>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          label={`${label} Command`}
          placeholder="FF0101"
          helperText={isHttp ? 'Sent as the URL path, eg. FF0101 or status.xml' : 'Hex'}
          value={value.command}
          onChange={(e) => onChange({ ...value, command: normaliseCommand(e.target.value) })}
          disabled={disabled}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 8 }}>
        <TextField
          fullWidth
          label={`${label} Description`}
          value={value.description}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          disabled={disabled}
        />
      </Grid>
    </>
  );
}

export default function RelayPeripheralConfig({ value, onChange, title, indicators = [], disabled = false }: RelayPeripheralConfigProps) {
  const [advancedMode, setAdvancedMode] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const advancedJson = useMemo(() => JSON.stringify(relayConfigToJson(value), null, 2), [value]);
  const [jsonText, setJsonText] = useState(advancedJson);

  const handleModeToggle = (checked: boolean) => {
    setAdvancedMode(checked);
    setJsonError(null);
    if (checked) {
      setJsonText(advancedJson);
    }
  };

  const handleApplyJson = () => {
    try {
      onChange(buildDefaultRelayConfig(JSON.parse(jsonText)));
      setJsonError(null);
    } catch {
      setJsonError('Configuration JSON is invalid. Fix the JSON and try again.');
    }
  };

  const updateSubscription = (evt: RelayEventValue, patch: Partial<RelayEventSubscription>) => {
    onChange({
      ...value,
      subscriptions: value.subscriptions.map((s) => (s.event === evt ? { ...s, ...patch } : s))
    });
  };

  const isHttp = value.portConfig.type === PORT_CONFIG_OPTIONS.Http;
  const upLabel = value.upCommand.description.trim() || 'Up';
  const downLabel = value.downCommand.description.trim() || 'Down';

  const selectedIndicator = indicators.find((indicator) => indicator.peripheralId === value.indicatorPeripheralId);
  // An indicator that is inactive, on another site or since removed is still shown, so the link can be seen and changed.
  const isUnknownIndicator = value.indicatorPeripheralId != null && selectedIndicator === undefined;
  const isUnknownPlatform =
    value.scaleIndex != null && (selectedIndicator === undefined || value.scaleIndex >= selectedIndicator.platforms.length);

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title} Configuration
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Simple
          </Typography>
          <Switch checked={advancedMode} onChange={(_, checked) => handleModeToggle(checked)} disabled={disabled} />
          <Typography variant="body2" color="text.secondary">
            Advanced
          </Typography>
        </Box>
      </Box>

      {!advancedMode ? (
        <Stack spacing={2.5}>
          <PortConfigComponent
            value={value.portConfig}
            onChange={(next) =>
              onChange({
                ...value,
                portConfig: next,
                // A web relay always has status.xml, so a relay switched to HTTP can show its state from the start.
                readStatusCommand:
                  next.type === PORT_CONFIG_OPTIONS.Http && !value.readStatusCommand.command.trim()
                    ? { command: 'status.xml', description: 'Read Status' }
                    : value.readStatusCommand
              })
            }
            allowedTypes={RELAY_PORT_TYPES}
            disabled={disabled}
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="number"
                label="Relay No"
                helperText="Relay on the board this device is wired to"
                value={value.portNo}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isInteger(n) && n > 0) onChange({ ...value, portNo: n });
                }}
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                select
                label="Position"
                value={value.position}
                onChange={(e) => onChange({ ...value, position: Number(e.target.value) })}
                disabled={disabled}
              >
                {POSITION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                select
                label="Direction"
                value={value.direction}
                onChange={(e) => onChange({ ...value, direction: Number(e.target.value) })}
                disabled={disabled}
              >
                {DIRECTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Divider />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Commands
              </Typography>
            </Grid>
            <CommandFields
              label="Up"
              value={value.upCommand}
              onChange={(next) => onChange({ ...value, upCommand: next })}
              disabled={disabled}
              isHttp={isHttp}
            />
            <CommandFields
              label="Down"
              value={value.downCommand}
              onChange={(next) => onChange({ ...value, downCommand: next })}
              disabled={disabled}
              isHttp={isHttp}
            />
            <CommandFields
              label="Read Status"
              value={value.readStatusCommand}
              onChange={(next) => onChange({ ...value, readStatusCommand: next })}
              disabled={disabled}
              isHttp={isHttp}
            />
          </Grid>

          <Divider />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Weight Feed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The indicator and platform whose vehicle tracking drives the bridge events. Leave on the default to use the first indicator
                on the device.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                select
                label="Indicator"
                value={value.indicatorPeripheralId ?? ''}
                // A platform belongs to one indicator, so choosing another starts again from its first.
                onChange={(e) => onChange({ ...value, indicatorPeripheralId: e.target.value || null, scaleIndex: null })}
                helperText={indicators.length === 0 ? 'No active indicators on this site' : undefined}
                disabled={disabled}
              >
                <MenuItem value="">
                  <em>Default — first indicator on the device</em>
                </MenuItem>
                {isUnknownIndicator ? (
                  <MenuItem value={value.indicatorPeripheralId ?? ''}>Unknown indicator ({value.indicatorPeripheralId})</MenuItem>
                ) : null}
                {indicators.map((indicator) => (
                  <MenuItem key={indicator.peripheralId} value={indicator.peripheralId}>
                    {indicator.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Platform"
                value={value.scaleIndex ?? ''}
                onChange={(e) => onChange({ ...value, scaleIndex: e.target.value === '' ? null : Number(e.target.value) })}
                helperText={
                  selectedIndicator && selectedIndicator.platforms.length === 0 ? 'This indicator has no platforms configured' : undefined
                }
                disabled={disabled || value.indicatorPeripheralId == null}
              >
                <MenuItem value="">
                  <em>First platform</em>
                </MenuItem>
                {isUnknownPlatform && value.scaleIndex != null ? (
                  <MenuItem value={value.scaleIndex}>Unknown platform ({value.scaleIndex + 1})</MenuItem>
                ) : null}
                {(selectedIndicator?.platforms ?? []).map((platform, index) => (
                  <MenuItem key={index} value={index}>
                    {platform || `Platform ${index + 1}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Divider />

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Event Subscriptions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select which events this {title.toLowerCase()} responds to. Leave Duration blank to stay in that state until the next event,
                or set it with Repeat to pulse or flash.
              </Typography>
            </Grid>

            {ALL_RELAY_EVENTS.map(({ value: evt, label }) => {
              const sub = value.subscriptions.find((s) => s.event === evt);
              const isEnabled = sub !== undefined;
              return (
                <Grid size={{ xs: 12 }} key={evt}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid size={{ xs: 'auto' }}>
                      <Checkbox
                        checked={isEnabled}
                        onChange={(_, checked) => {
                          if (checked) {
                            const newSub: RelayEventSubscription = {
                              event: evt,
                              action: RelayAction.Up,
                              durationSeconds: null,
                              repeatCount: 1
                            };
                            onChange({ ...value, subscriptions: [...value.subscriptions, newSub] });
                          } else {
                            onChange({ ...value, subscriptions: value.subscriptions.filter((s) => s.event !== evt) });
                          }
                        }}
                        disabled={disabled}
                        size="small"
                      />
                    </Grid>
                    <Grid size={{ xs: 4, md: 2 }}>
                      <Typography variant="body2" color={isEnabled ? 'text.primary' : 'text.disabled'}>
                        {label}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <TextField
                        fullWidth
                        select
                        size="small"
                        label="Action"
                        value={sub?.action ?? ''}
                        onChange={(e) => updateSubscription(evt, { action: Number(e.target.value) as RelayActionValue })}
                        disabled={disabled || !isEnabled}
                      >
                        <MenuItem value={RelayAction.Up}>{upLabel}</MenuItem>
                        <MenuItem value={RelayAction.Down}>{downLabel}</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Duration (s)"
                        placeholder="Latch"
                        value={sub?.durationSeconds ?? ''}
                        onChange={(e) => {
                          if (e.target.value === '') {
                            updateSubscription(evt, { durationSeconds: null });
                            return;
                          }
                          const n = Number(e.target.value);
                          if (Number.isFinite(n) && n >= 0) updateSubscription(evt, { durationSeconds: n > 0 ? n : null });
                        }}
                        disabled={disabled || !isEnabled}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Repeat"
                        value={isEnabled ? sub.repeatCount : ''}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          if (Number.isInteger(n) && n >= 1) updateSubscription(evt, { repeatCount: n });
                        }}
                        disabled={disabled || !isEnabled || sub?.durationSeconds == null}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>

          <Divider />

          <LinkedDevicesConfig
            value={value.linkedDevices}
            onChange={(next) => onChange({ ...value, linkedDevices: next })}
            disabled={disabled}
          />
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <TextField
            fullWidth
            multiline
            minRows={18}
            label="Configuration JSON"
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            disabled={disabled}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={handleApplyJson} disabled={disabled}>
              Apply JSON
            </Button>
          </Box>
          {jsonError ? <Alert severity="error">{jsonError}</Alert> : null}
        </Stack>
      )}
    </Stack>
  );
}
